/**
 * Trainova AI — Client Admin & Super Admin Reports Page
 * Prototype Reference: trainova-client-admin-portal-prototype.html#reports
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useTranslation } from '../../../i18n';
import { api } from '../../../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { Pagination } from '../../../components/ui/Pagination';
import '../admin-portal.css';

export interface CompetencyMetric {
  key: string;
  name: string;
  weight: number;
  targetScore: number;
  avgScore: number;
  avgPercentage?: number;
  evaluationsCount?: number;
}

export interface LearnerReportRow {
  userId: string;
  name: string;
  email: string;
  employeeId: string;
  team: string;
  designation: string;
  region: string;
  attemptsCount: number;
  maxAttempts: number;
  bestScore: number | null;
  competencyScores: Record<string, number | null>;
  result: 'Passed' | 'Not passed' | 'In progress' | 'Not started';
  lastAttemptDate: string;
  lastAttemptId: string | null;
}

export interface TenantReportGroup {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  metrics: {
    assigned: number;
    completed: number;
    passed: number;
    notPassed: number;
    inProgress: number;
    notStarted: number;
    passRate: number;
    avgScore: number;
    avgDurationMinutes: number;
    distribution: {
      passed: number;
      notPassed: number;
      inProgress: number;
      notStarted: number;
    };
  };
  learners: LearnerReportRow[];
}

const AVATAR_COLORS = ['#3B5BDB', '#0F8A5F', '#B26A00', '#C2477A', '#6A3FD1', '#0E7C86', '#4B5568', '#2F68C9'];

// Legacy report data can repeat a learner or a scenario. Keep one row per id so
// React keys stay unique and cohort totals are not double-counted.
const dedupeRowsById = <T,>(rows: T[], getId: (row: T) => string | undefined | null): T[] => {
  const seen = new Set<string>();
  const unique: T[] = [];
  for (const row of rows) {
    const id = getId(row);
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    unique.push(row);
  }
  return unique;
};

export const EXPORT_COMPETENCY_COLUMNS = [
  { key: 'EMPATHY', name: 'Empathy & Urgency Handling' },
  { key: 'OWNERSHIP', name: 'Ownership & Investigation' },
  { key: 'NEXT_STEPS', name: 'Closing & Next Steps' },
  { key: 'ISSUE_DISCOVERY', name: 'Issue Discovery & Verification' },
  { key: 'SAFE_COMMUNICATION', name: 'Clear & Secure Communication' },
];

export const getLearnerCompScore = (learner: any, comp: CompetencyMetric): number | null => {
  if (!learner || !learner.competencyScores) return null;
  const scores = learner.competencyScores;
  const candidateKeys: string[] = [
    comp.key,
    comp.key?.toLowerCase(),
    comp.key?.toUpperCase(),
    comp.key?.replace(/^crit_/i, ''),
    comp.key?.replace(/^crit_/i, '').toLowerCase(),
    comp.key?.replace(/^crit_/i, '').toUpperCase(),
    comp.name,
    comp.name?.toLowerCase(),
    comp.name?.toUpperCase(),
    comp.name?.toLowerCase().replace(/[^a-z0-9]/g, '_'),
    comp.name?.toLowerCase().replace(/[^a-z0-9]/g, ''),
  ].filter((k): k is string => Boolean(k));

  const words = (comp.name || '').trim().split(/\s+/);
  if (words[0] && words[0].length > 2) {
    candidateKeys.push(words[0], words[0].toLowerCase(), words[0].toUpperCase());
  }
  if (words.length >= 2) {
    const two = `${words[0]} ${words[1]}`;
    candidateKeys.push(two, two.toLowerCase(), two.toUpperCase());
  }

  for (const k of candidateKeys) {
    if (scores[k] !== undefined && scores[k] !== null) {
      const val = Number(scores[k]);
      if (!isNaN(val) && val >= 0) return val;
    }
  }
  return null;
}

export function buildLearnerResultsExportMatrix(
  learners: LearnerReportRow[],
  competencies: CompetencyMetric[],
  getScoreFn: (learner: any, comp: any) => number | null = getLearnerCompScore
) {
  const headers = [
    'Learner Name',
    'Employee ID',
    'Email',
    'Team',
    'Designation',
    'Region',
    'Attempts',
    'Overall Score',
    'Result',
    'Last Attempt Date',
    'Empathy & Urgency Handling',
    'Ownership & Investigation',
    'Closing & Next Steps',
    'Issue Discovery & Verification',
    'Clear & Secure Communication',
  ];

  const rows = learners.map((l) => {
    const compScores = EXPORT_COMPETENCY_COLUMNS.map((targetComp) => {
      const matchedComp = competencies.find((c) => {
        const cName = (c.name || '').toLowerCase();
        const tName = targetComp.name.toLowerCase();
        const cKey = (c.key || '').toLowerCase();
        const tKey = targetComp.key.toLowerCase();
        return cKey === tKey || cName === tName || cName.includes(targetComp.name.split(' ')[0].toLowerCase());
      }) || targetComp;

      const score = getScoreFn(l, matchedComp);
      return score !== null && score !== undefined && !isNaN(score) ? score : '';
    });

    return [
      l.name || 'Learner',
      l.employeeId || '—',
      l.email || '—',
      l.team || '—',
      l.designation || '—',
      l.region || '—',
      `${l.attemptsCount || 0}/${l.maxAttempts || 3}`,
      l.bestScore !== null && l.bestScore !== undefined ? `${l.bestScore}%` : '—',
      l.result || 'Not started',
      l.lastAttemptDate || '—',
      ...compScores,
    ];
  });

  return { headers, rows };
}

export function AdminReportsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPlatformAdmin } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL query params
  const urlSimId = searchParams.get('simulationId') || '';
  const urlTenantId = searchParams.get('tenantId') || (isPlatformAdmin ? 'all' : '');

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active Selected Scope
  const [simulations, setSimulations] = useState<Array<{ id: string; title: string; category: string; status: string }>>([]);
  const [selectedSimId, setSelectedSimId] = useState<string>(urlSimId);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(urlTenantId);

  // Searchable Dropdown Popup States
  const [isSimDropdownOpen, setIsSimDropdownOpen] = useState(false);
  const [simSearchText, setSimSearchText] = useState('');
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const [tenantSearchText, setTenantSearchText] = useState('');

  // Table Filters (Client-side instant filtering)
  const [teamFilter, setTeamFilter] = useState('All');
  const [designationFilter, setDesignationFilter] = useState('All');
  const [regionFilter, setRegionFilter] = useState('All');
  const [resultFilter, setResultFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const activeFilterCount = (teamFilter !== 'All' ? 1 : 0) +
    (designationFilter !== 'All' ? 1 : 0) +
    (regionFilter !== 'All' ? 1 : 0) +
    (resultFilter !== 'All' ? 1 : 0);

  // Data
  const [competencies, setCompetencies] = useState<CompetencyMetric[]>([]);
  const [serverFilterOptions, setServerFilterOptions] = useState<{ teams: string[]; designations: string[]; regions: string[] }>({
    teams: [],
    designations: [],
    regions: [],
  });
  const [metrics, setMetrics] = useState({
    assigned: 0,
    completed: 0,
    passed: 0,
    notPassed: 0,
    inProgress: 0,
    notStarted: 0,
    passRate: 0,
    avgScore: 0,
    avgDurationMinutes: 0,
    distribution: { passed: 0, notPassed: 0, inProgress: 0, notStarted: 0 },
  });
  const [learners, setLearners] = useState<LearnerReportRow[]>([]);
  const [tenantReports, setTenantReports] = useState<TenantReportGroup[]>([]);
  const [expandedTenantIds, setExpandedTenantIds] = useState<Set<string>>(new Set());

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Simulation & organization picker popovers (outside-click ownership)
  const simPickerRef = useRef<HTMLDivElement>(null);
  const tenantPickerRef = useRef<HTMLDivElement>(null);

  // Sync state if URL searchParams change
  useEffect(() => {
    const qSimId = searchParams.get('simulationId') || '';
    const qTenantId = searchParams.get('tenantId') || (isPlatformAdmin ? 'all' : '');
    if (qSimId !== selectedSimId) {
      setSelectedSimId(qSimId);
    }
    if (qTenantId !== selectedTenantId) {
      setSelectedTenantId(qTenantId);
    }
  }, [searchParams, isPlatformAdmin]);

  // The pickers must dismiss on an outside click or Escape. Without this they
  // keep floating over the report (and over the loading card) with no way to
  // close them.
  useEffect(() => {
    if (!isSimDropdownOpen && !isTenantDropdownOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (simPickerRef.current && !simPickerRef.current.contains(target)) setIsSimDropdownOpen(false);
      if (tenantPickerRef.current && !tenantPickerRef.current.contains(target)) setIsTenantDropdownOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsSimDropdownOpen(false);
      setIsTenantDropdownOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSimDropdownOpen, isTenantDropdownOpen]);

  // A refetch rebuilds both option lists, so never leave a picker open above
  // the loading placeholder.
  useEffect(() => {
    if (!isLoading) return;
    setIsSimDropdownOpen(false);
    setIsTenantDropdownOpen(false);
  }, [isLoading]);

  // Load report data
  useEffect(() => {
    let isCancelled = false;

    const fetchReports = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.admin.getReports({
          simulationId: selectedSimId || undefined,
          tenantId: isPlatformAdmin ? (selectedTenantId || 'all') : undefined,
          team: teamFilter !== 'All' ? teamFilter : undefined,
          designation: designationFilter !== 'All' ? designationFilter : undefined,
          region: regionFilter !== 'All' ? regionFilter : undefined,
          result: resultFilter !== 'All' ? resultFilter : undefined,
          search: debouncedSearch.trim() || undefined,
        });

        if (isCancelled) return;

        const d: any = (res as any)?.data?.data || (res as any)?.data || res;

        if (d) {
          if (Array.isArray(d.simulations) && d.simulations.length > 0) {
            setSimulations(dedupeRowsById(d.simulations, (s: any) => s?.id));
          }

          // If no simulation was explicitly selected in URL, auto-select the one returned or first in list
          const activeId = d.selectedSimulation?.id || d.simulations?.[0]?.id || '';
          if (!selectedSimId && activeId) {
            setSelectedSimId(activeId);
          }

          if (d.competencies && Array.isArray(d.competencies)) {
            setCompetencies(d.competencies);
          } else {
            setCompetencies([]);
          }

          if (d.filterOptions) {
            setServerFilterOptions((prev) => ({
              teams: Array.from(new Set([...prev.teams, ...(d.filterOptions.teams || [])])).sort(),
              designations: Array.from(new Set([...prev.designations, ...(d.filterOptions.designations || [])])).sort(),
              regions: Array.from(new Set([...prev.regions, ...(d.filterOptions.regions || [])])).sort(),
            }));
          }

          if (d.tenantReports && Array.isArray(d.tenantReports) && d.tenantReports.length > 0) {
            const uniqueTenantReports = dedupeRowsById(d.tenantReports, (t: any) => t?.tenantId).map((t: any) => ({
              ...t,
              learners: dedupeRowsById(t.learners || [], (l: any) => l?.userId),
            }));
            setTenantReports(uniqueTenantReports);
            setExpandedTenantIds(new Set([uniqueTenantReports[0].tenantId]));
          } else {
            setTenantReports([]);
          }

          if (isPlatformAdmin && (!selectedTenantId || selectedTenantId === 'all')) {
            setMetrics(d.globalMetrics || d.metrics || {
              assigned: 0,
              completed: 0,
              passed: 0,
              notPassed: 0,
              inProgress: 0,
              notStarted: 0,
              passRate: 0,
              avgScore: 0,
              avgDurationMinutes: 0,
              distribution: { passed: 0, notPassed: 0, inProgress: 0, notStarted: 0 },
            });
            const allL = (d.tenantReports || []).flatMap((t: any) => t.learners || []);
            const flatLearners = allL.length > 0 ? allL : (d.learners || []);
            setLearners(dedupeRowsById(flatLearners, (l: any) => l?.userId));
          } else {
            setMetrics(d.metrics || d.globalMetrics || {
              assigned: 0,
              completed: 0,
              passed: 0,
              notPassed: 0,
              inProgress: 0,
              notStarted: 0,
              passRate: 0,
              avgScore: 0,
              avgDurationMinutes: 0,
              distribution: { passed: 0, notPassed: 0, inProgress: 0, notStarted: 0 },
            });
            setLearners(dedupeRowsById(
              (d.learners && d.learners.length > 0) ? d.learners : (d.tenantReports?.[0]?.learners || []),
              (l: any) => l?.userId
            ));
          }
        }
      } catch (err: any) {
        if (!isCancelled) {
          setError(err.message || 'Failed to fetch reports');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchReports();
    return () => {
      isCancelled = true;
    };
  }, [selectedSimId, selectedTenantId, isPlatformAdmin, teamFilter, designationFilter, regionFilter, resultFilter, debouncedSearch]);

  // Unique filter values derived from base learners and server options
  const filterOptions = useMemo(() => {
    const teams = Array.from(new Set([...serverFilterOptions.teams, ...learners.map((l) => l.team).filter(Boolean)])).sort();
    const designations = Array.from(new Set([...serverFilterOptions.designations, ...learners.map((l) => l.designation).filter(Boolean)])).sort();
    const regions = Array.from(new Set([...serverFilterOptions.regions, ...learners.map((l) => l.region).filter(Boolean)])).sort();
    return { teams, designations, regions };
  }, [learners, serverFilterOptions]);

  // Filtered learners
  const filteredLearners = useMemo(() => {
    return learners.filter((l) => {
      if (teamFilter !== 'All' && l.team !== teamFilter) return false;
      if (designationFilter !== 'All' && l.designation !== designationFilter) return false;
      if (regionFilter !== 'All' && l.region !== regionFilter) return false;
      if (resultFilter !== 'All' && l.result !== resultFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchName = (l.name || '').toLowerCase().includes(q);
        const matchId = (l.employeeId || '').toLowerCase().includes(q);
        const matchEmail = (l.email || '').toLowerCase().includes(q);
        const matchTeam = (l.team || '').toLowerCase().includes(q);
        const matchDesig = (l.designation || '').toLowerCase().includes(q);
        if (!matchName && !matchId && !matchEmail && !matchTeam && !matchDesig) return false;
      }
      return true;
    });
  }, [learners, teamFilter, designationFilter, regionFilter, resultFilter, searchQuery]);

  // Paginated learners
  const paginatedLearners = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredLearners.slice(start, start + pageSize);
  }, [filteredLearners, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredLearners.length / pageSize) || 1;

  // A narrower scope or filter can strand the table beyond the last page, which
  // renders an empty table with no explanation.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  // Change simulation handler
  const handleSelectSimulation = (simId: string) => {
    setSelectedSimId(simId);
    setIsSimDropdownOpen(false);
    setSimSearchText('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('simulationId', simId);
      if (selectedTenantId) next.set('tenantId', selectedTenantId);
      return next;
    });
  };

  // Change organization handler (Super Admin)
  const handleSelectTenant = (tId: string) => {
    setSelectedTenantId(tId);
    setIsTenantDropdownOpen(false);
    setTenantSearchText('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (selectedSimId) next.set('simulationId', selectedSimId);
      next.set('tenantId', tId);
      return next;
    });
  };

  // Accordion toggle helper for Super Admin
  const toggleTenantAccordion = (tenantId: string) => {
    const next = new Set(expandedTenantIds);
    if (next.has(tenantId)) {
      next.delete(tenantId);
    } else {
      next.add(tenantId);
    }
    setExpandedTenantIds(next);
  };

  // Export handlers
  const handleDownloadExcel = () => {
    const { headers, rows } = buildLearnerResultsExportMatrix(filteredLearners, competencies, getLearnerCompScore);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [
      { wch: 22 }, // Learner Name
      { wch: 15 }, // Employee ID
      { wch: 26 }, // Email
      { wch: 18 }, // Team
      { wch: 20 }, // Designation
      { wch: 14 }, // Region
      { wch: 12 }, // Attempts
      { wch: 14 }, // Overall Score
      { wch: 14 }, // Result
      { wch: 18 }, // Last Attempt Date
      { wch: 28 }, // Empathy & Urgency Handling
      { wch: 28 }, // Ownership & Investigation
      { wch: 24 }, // Closing & Next Steps
      { wch: 30 }, // Issue Discovery & Verification
      { wch: 30 }, // Clear & Secure Communication
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Learner Results');

    const filename = `Trainova_Simulation_Report_${selectedSimId || 'all'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    try {
      XLSX.writeFile(wb, filename);
    } catch {
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadDataDump = () => {
    const dump = {
      exportTimestamp: new Date().toISOString(),
      simulationId: selectedSimId,
      tenantId: selectedTenantId,
      metrics,
      competencies,
      learners: filteredLearners,
      tenantReports: isPlatformAdmin ? tenantReports : undefined,
    };
    const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Trainova_Data_Dump_${selectedSimId || 'all'}_${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const clearAllFilters = () => {
    setTeamFilter('All');
    setDesignationFilter('All');
    setRegionFilter('All');
    setResultFilter('All');
    setSearchQuery('');
  };

  const getScorePillClass = (score: number | null) => {
    if (score === null || score === undefined) return 'ad-n';
    if (score >= 4.5) return 'ad-h';
    if (score >= 3.5) return 'ad-m';
    if (score >= 2.5) return 'ad-l';
    return 'ad-x';
  };

  const getResultBadgeClass = (res: string) => {
    if (res === 'Passed') return 'ad-p';
    if (res === 'Not passed') return 'ad-f';
    if (res === 'In progress') return 'ad-ip';
    return 'ad-ns';
  };

  const dist = metrics.distribution || { passed: 0, notPassed: 0, inProgress: 0, notStarted: 0 };
  const totalDistCount = (dist.passed || 0) + (dist.notPassed || 0) + (dist.inProgress || 0) + (dist.notStarted || 0);
  const totalLearnersCount = totalDistCount > 0 ? totalDistCount : (metrics.assigned || (learners.length > 0 ? learners.length : 1));

  const passedPct = totalLearnersCount > 0 ? Math.round(((dist.passed || 0) / totalLearnersCount) * 100) : 0;
  const notPassedPct = totalLearnersCount > 0 ? Math.round(((dist.notPassed || 0) / totalLearnersCount) * 100) : 0;
  const inProgressPct = totalLearnersCount > 0 ? Math.round(((dist.inProgress || 0) / totalLearnersCount) * 100) : 0;
  const notStartedPct = totalLearnersCount > 0 ? Math.max(0, 100 - passedPct - notPassedPct - inProgressPct) : 0;

  const activeSimulation = simulations.find((s) => s.id === selectedSimId);
  const activeTenant = tenantReports.find((t) => t.tenantId === selectedTenantId);

  const filteredSimulations = simulations.filter((s) =>
    (s.title || '').toLowerCase().includes(simSearchText.toLowerCase()) ||
    (s.category || '').toLowerCase().includes(simSearchText.toLowerCase()) ||
    (s.id || '').toLowerCase().includes(simSearchText.toLowerCase())
  );

  const filteredTenants = tenantReports.filter((t) =>
    (t.tenantName || '').toLowerCase().includes(tenantSearchText.toLowerCase()) ||
    (t.tenantSlug || '').toLowerCase().includes(tenantSearchText.toLowerCase()) ||
    (t.tenantId || '').toLowerCase().includes(tenantSearchText.toLowerCase())
  );

  return (
    <div className="ad-reports-container ad-g-rep" style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
      {/* Standardized Admin Header & Banner */}
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Simulation & Competency Reports' },
        ]}
        icon="📋"
        title="Simulation & Competency Reports"
        badge={isPlatformAdmin ? 'Super Admin Master View' : 'Client Admin View'}
        description={
          isPlatformAdmin
            ? 'Cross-organization analytics, completion cohorts, and scenario competency metrics.'
            : 'Results and competency performance for your organization learners by simulation.'
        }
        actions={
          <div className="ad-fl" style={{ display: 'flex', gap: '8px' }}>
            <button
              className="ad-btn"
              onClick={handleDownloadExcel}
              disabled={!selectedSimId || isLoading || filteredLearners.length === 0}
              style={{
                background: !selectedSimId ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.15)',
                color: '#FFFFFF',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                opacity: !selectedSimId ? 0.6 : 1,
                cursor: !selectedSimId ? 'not-allowed' : 'pointer',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
              </svg>
              Download Excel
            </button>
            <button
              className="ad-btn"
              onClick={handleDownloadDataDump}
              disabled={!selectedSimId || isLoading}
              style={{
                background: !selectedSimId ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.15)',
                color: '#FFFFFF',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                opacity: !selectedSimId ? 0.6 : 1,
                cursor: !selectedSimId ? 'not-allowed' : 'pointer',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
              </svg>
              Download data dump
            </button>
          </div>
        }
      />
      {error && (
        <div style={{ padding: '12px 16px', background: 'var(--bad-soft)', color: 'var(--bad)', borderRadius: '8px', fontSize: '13px', fontWeight: 600 }}>
          {error}
        </div>
      )}

      {/* 2. Filter Toolbar with Searchable Simulation Selector & Searchable Organization Selector */}
      <div className="ad-flt" style={{ flexWrap: 'wrap', gap: '8px 12px' }}>
        {/* Mobile Filter Button */}
        <button
          type="button"
          className="ad-mobile-filter-btn"
          onClick={() => setIsMobileFilterOpen(true)}
          aria-label="Open report filters"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="21" x2="4" y2="14" />
            <line x1="4" y1="10" x2="4" y2="3" />
            <line x1="12" y1="21" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12" y2="3" />
            <line x1="20" y1="21" x2="20" y2="16" />
            <line x1="20" y1="12" x2="20" y2="3" />
            <line x1="1" y1="14" x2="7" y2="14" />
            <line x1="9" y1="8" x2="15" y2="8" />
            <line x1="17" y1="16" x2="23" y2="16" />
          </svg>
          <span>{t('reports.filters', undefined, 'Filters')}</span>
          {activeFilterCount > 0 && <span className="ad-filter-badge">{activeFilterCount}</span>}
        </button>

        {/* Searchable Simulation Combobox */}
        <div style={{ position: 'relative' }} ref={simPickerRef}>
          <button
            type="button"
            className="ad-sel ad-pri2"
            aria-haspopup="listbox"
            aria-expanded={isSimDropdownOpen}
            onClick={() => {
              setIsSimDropdownOpen(!isSimDropdownOpen);
              setIsTenantDropdownOpen(false);
            }}
            style={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 600,
              background: '#FFFFFF',
            }}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12z" />
              <path d="m10.5 9 4 3-4 3z" fill="currentColor" />
            </svg>
            <span>{t('reports.filter_simulation', undefined, 'Simulation:')}</span>
            <strong style={{ color: selectedSimId ? 'var(--navy)' : 'var(--muted)', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeSimulation?.title || t('reports.select_simulation', undefined, 'Select Simulation…')}
            </strong>
            <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{isSimDropdownOpen ? '▲' : '▼'}</span>
          </button>

          {isSimDropdownOpen && (
            <div
              role="listbox"
              aria-label="Simulations"
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                width: 'min(320px, calc(100vw - 32px))',
                maxHeight: '340px',
                backgroundColor: '#FFFFFF',
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                border: '1px solid #E2E8F0',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '8px 10px', borderBottom: '1px solid #F1F5F9' }}>
                <input
                  type="text"
                  placeholder={t('reports.search_sim_placeholder', undefined, 'Search simulation title, category, ID...')}
                  value={simSearchText}
                  onChange={(e) => setSimSearchText(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: '12.5px',
                    borderRadius: '6px',
                    border: '1px solid #CBD5E1',
                    outline: 'none',
                  }}
                />
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }} role="presentation">
                {filteredSimulations.length === 0 ? (
                  <div style={{ padding: '12px', fontSize: '12px', color: '#94A3B8', textAlign: 'center' }}>
                    {t('reports.no_sims_found', undefined, 'No simulations found matching search')}
                  </div>
                ) : (
                  filteredSimulations.map((s) => {
                    const isSelected = selectedSimId === s.id;
                    return (
                      <div
                        key={s.id}
                        role="option"
                        aria-selected={isSelected}
                        title={s.title}
                        onClick={() => handleSelectSimulation(s.id)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#EEF2FF' : 'transparent',
                          color: isSelected ? '#3730A3' : '#1E293B',
                          fontSize: '12.5px',
                          fontWeight: isSelected ? 700 : 500,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderBottom: '1px solid #F8FAFC',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) e.currentTarget.style.backgroundColor = '#F8FAFC';
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.title}</div>
                          {/* Several scenarios share a title, so the row also shows
                              the category and the id tail to tell them apart. */}
                          <span style={{ fontSize: '10.5px', color: '#64748B' }}>
                            {s.category || 'General'} · #{(s.id || '').slice(-6)}
                          </span>
                        </div>
                        {isSelected && <span style={{ color: '#4F46E5', fontSize: '12px' }}>✓</span>}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Super Admin Searchable Organization Selector */}
        {isPlatformAdmin && (
          <div style={{ position: 'relative' }} ref={tenantPickerRef}>
            <button
              type="button"
              className="ad-sel"
              aria-haspopup="listbox"
              aria-expanded={isTenantDropdownOpen}
              onClick={() => {
                setIsTenantDropdownOpen(!isTenantDropdownOpen);
                setIsSimDropdownOpen(false);
              }}
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 600,
                background: '#FFFFFF',
              }}
            >
              <span>🏢 {t('reports.filter_organization', undefined, 'Organization:')}</span>
              <strong style={{ color: 'var(--navy)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedTenantId === 'all' || !selectedTenantId
                  ? t('reports.all_organizations', undefined, 'All Organizations')
                  : activeTenant?.tenantName || selectedTenantId}
              </strong>
              <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{isTenantDropdownOpen ? '▲' : '▼'}</span>
            </button>

            {isTenantDropdownOpen && (
              <div
                role="listbox"
                aria-label="Organizations"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  width: 'min(300px, calc(100vw - 32px))',
                  maxHeight: '340px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '10px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                  border: '1px solid #E2E8F0',
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '8px 10px', borderBottom: '1px solid #F1F5F9' }}>
                  <input
                    type="text"
                    placeholder={t('reports.search_org_placeholder', undefined, 'Search organization name, ID, slug...')}
                    value={tenantSearchText}
                    onChange={(e) => setTenantSearchText(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '12.5px',
                      borderRadius: '6px',
                      border: '1px solid #CBD5E1',
                      outline: 'none',
                    }}
                  />
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }} role="presentation">
                  {/* All Organizations option */}
                  <div
                    onClick={() => handleSelectTenant('all')}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: selectedTenantId === 'all' ? '#EEF2FF' : 'transparent',
                      color: selectedTenantId === 'all' ? '#3730A3' : '#1E293B',
                      fontSize: '12.5px',
                      fontWeight: selectedTenantId === 'all' ? 700 : 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #F1F5F9',
                    }}
                  >
                    <span>{t('reports.all_orgs_cross_tenant', undefined, '🌐 All Organizations (Cross-Tenant)')}</span>
                    {selectedTenantId === 'all' && <span style={{ color: '#4F46E5' }}>✓</span>}
                  </div>

                  {filteredTenants.length === 0 ? (
                    <div style={{ padding: '12px', fontSize: '12px', color: '#94A3B8', textAlign: 'center' }}>
                      {t('reports.no_orgs_found', undefined, 'No organizations matching search')}
                    </div>
                  ) : (
                    filteredTenants.map((tRow) => {
                      const isSelected = selectedTenantId === tRow.tenantId;
                      return (
                        <div
                          key={tRow.tenantId}
                          role="option"
                          aria-selected={isSelected}
                          title={`${tRow.tenantName} (${tRow.tenantId})`}
                          onClick={() => handleSelectTenant(tRow.tenantId)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#EEF2FF' : 'transparent',
                            color: isSelected ? '#3730A3' : '#1E293B',
                            fontSize: '12.5px',
                            fontWeight: isSelected ? 700 : 500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: '1px solid #F8FAFC',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = '#F8FAFC';
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tRow.tenantName}</div>
                            {/* Registry names repeat across test organizations; the
                                slug plus id tail keeps each row distinguishable. */}
                            <span style={{ fontSize: '10.5px', color: '#64748B' }}>
                              {(tRow.tenantSlug || tRow.tenantId)} · #{(tRow.tenantId || '').slice(-6)}
                            </span>
                          </div>
                          {isSelected && <span style={{ color: '#4F46E5' }}>✓</span>}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Team Selector */}
        <span className="ad-sel" style={{ opacity: !selectedSimId ? 0.6 : 1 }}>
          {t('reports.filter_team', undefined, 'Team:')}{' '}
          <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} disabled={!selectedSimId}>
            <option value="All">{t('table.filter_all', undefined, 'All')} {filterOptions.teams.length > 0 ? `(${filterOptions.teams.length})` : ''}</option>
            {filterOptions.teams.map((teamName) => (
              <option key={teamName} value={teamName}>{teamName}</option>
            ))}
          </select>
        </span>

        {/* Designation UDF Selector */}
        <span className="ad-sel ad-uf" style={{ opacity: !selectedSimId ? 0.6 : 1 }}>
          {t('reports.filter_designation', undefined, 'Designation:')}{' '}
          <select value={designationFilter} onChange={(e) => setDesignationFilter(e.target.value)} disabled={!selectedSimId}>
            <option value="All">{t('table.filter_all', undefined, 'All')} {filterOptions.designations.length > 0 ? `(${filterOptions.designations.length})` : ''}</option>
            {filterOptions.designations.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </span>

        {/* Region UDF Selector */}
        <span className="ad-sel ad-uf" style={{ opacity: !selectedSimId ? 0.6 : 1 }}>
          {t('reports.filter_region', undefined, 'Region:')}{' '}
          <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} disabled={!selectedSimId}>
            <option value="All">{t('table.filter_all', undefined, 'All')} {filterOptions.regions.length > 0 ? `(${filterOptions.regions.length})` : ''}</option>
            {filterOptions.regions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </span>

        {/* Result Selector */}
        <span className="ad-sel" style={{ opacity: !selectedSimId ? 0.6 : 1 }}>
          {t('reports.filter_result', undefined, 'Result:')}{' '}
          <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)} disabled={!selectedSimId}>
            <option value="All">{t('reports.all_results', undefined, 'All Results')}</option>
            <option value="Passed">{t('status.passed', undefined, 'Passed')}</option>
            <option value="Not passed">{t('status.not_passed', undefined, 'Not passed')}</option>
            <option value="In progress">{t('status.in_progress', undefined, 'In progress')}</option>
            <option value="Not started">{t('status.not_started', undefined, 'Not started')}</option>
          </select>
        </span>

        {/* Clear Filters Button */}
        <span className="ad-clr2" onClick={clearAllFilters} style={{ cursor: 'pointer' }}>
          {t('reports.clear_filters', undefined, 'Clear')}
        </span>
      </div>

      {/* Mobile Filter Modal Sheet */}
      {isMobileFilterOpen && (
        <div className="ad-mobile-filter-modal" onClick={() => setIsMobileFilterOpen(false)}>
          <div className="ad-mobile-filter-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="ad-mobile-filter-header">
              <h3>Filter Reports</h3>
              <button
                type="button"
                className="ad-mobile-filter-close"
                onClick={() => setIsMobileFilterOpen(false)}
                aria-label="Close filters"
              >
                ✕
              </button>
            </div>

            <div className="ad-mobile-filter-body">
              <div className="ad-mobile-filter-group">
                <label>Team</label>
                <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} disabled={!selectedSimId}>
                  <option value="All">All Teams {filterOptions.teams.length > 0 ? `(${filterOptions.teams.length})` : ''}</option>
                  {filterOptions.teams.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="ad-mobile-filter-group">
                <label>Designation</label>
                <select value={designationFilter} onChange={(e) => setDesignationFilter(e.target.value)} disabled={!selectedSimId}>
                  <option value="All">All Designations {filterOptions.designations.length > 0 ? `(${filterOptions.designations.length})` : ''}</option>
                  {filterOptions.designations.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div className="ad-mobile-filter-group">
                <label>Region</label>
                <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} disabled={!selectedSimId}>
                  <option value="All">All Regions {filterOptions.regions.length > 0 ? `(${filterOptions.regions.length})` : ''}</option>
                  {filterOptions.regions.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="ad-mobile-filter-group">
                <label>Result Status</label>
                <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)} disabled={!selectedSimId}>
                  <option value="All">All Results</option>
                  <option value="Passed">Passed</option>
                  <option value="Not passed">Not passed</option>
                  <option value="In progress">In progress</option>
                  <option value="Not started">Not started</option>
                </select>
              </div>
            </div>

            <div className="ad-mobile-filter-actions">
              <button
                type="button"
                className="ad-btn"
                style={{ flex: 1, background: 'var(--surface)', color: 'var(--ink)' }}
                onClick={() => {
                  clearAllFilters();
                }}
              >
                Clear all
              </button>
              <button
                type="button"
                className="ad-btn ad-btn-primary"
                style={{ flex: 1, background: 'var(--blue)', color: '#fff' }}
                onClick={() => setIsMobileFilterOpen(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Content: Loading State vs Selection Prompt vs Full Reports Dashboard */}
      {isLoading ? (
        <div className="adm-card" style={{ padding: '60px 24px', textAlign: 'center', margin: '20px 0' }}>
          <div className="dashboard-spinner" style={{ margin: '0 auto 16px', width: '36px', height: '36px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--navy)', marginBottom: '6px' }}>
            Loading Simulation & Competency Reports…
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
            Fetching analytics, learner scores, and competency distributions.
          </p>
        </div>
      ) : (!selectedSimId && simulations.length === 0) ? (
        <div className="adm-card" style={{ padding: '48px 32px', textAlign: 'center', margin: '20px 0' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(99, 102, 241, 0.2) 100%)',
              color: '#4F46E5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              margin: '0 auto 16px',
            }}
          >
            📊
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--navy)', marginBottom: '8px' }}>
            No Simulation Reports Available
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', maxWidth: '580px', margin: '0 auto 28px', lineHeight: 1.5 }}>
            No simulations found for the selected scope. Publish a simulation or select another tenant organization to view learner reports.
          </p>
        </div>
      ) : (
        <>
          {/* 3. KPI Summary Strip matching Prototype `ad-kp` & attempts-stat-card tokens */}
          <div className="ad-kp">
            <div className="ad-k">
              <div className="ad-k__icon" style={{ background: 'var(--blue-soft)', color: 'var(--blue)' }}>👥</div>
              <div className="ad-k__content">
                <small>Assigned</small>
                <div className="ad-v">
                  <b>{metrics.assigned}</b>
                  <span>learners</span>
                </div>
                <span className="ad-nt">{metrics.completed} completed · {metrics.inProgress} in progress</span>
              </div>
            </div>

            <div className="ad-k">
              <div className="ad-k__icon" style={{ background: 'var(--ok-soft)', color: 'var(--ok)' }}>🎯</div>
              <div className="ad-k__content">
                <small>Pass rate</small>
                <div className="ad-v">
                  <b style={{ color: metrics.passRate >= 70 ? 'var(--ok)' : 'var(--warn)' }}>{metrics.passRate}%</b>
                  <span>passed</span>
                </div>
                <span className="ad-nt">{metrics.passed} of {metrics.passed + metrics.notPassed} evaluated · target 70%</span>
              </div>
            </div>

            <div className="ad-k">
              <div className="ad-k__icon" style={{ background: '#EEF2FF', color: '#6A3FD1' }}>⭐</div>
              <div className="ad-k__content">
                <small>Average score</small>
                <div className="ad-v">
                  <b>{metrics.avgScore}%</b>
                  <span>overall</span>
                </div>
                <span className="ad-nt">Across all completed attempts</span>
              </div>
            </div>

            <div className="ad-k">
              <div className="ad-k__icon" style={{ background: 'var(--blue-soft)', color: 'var(--blue)' }}>⏱️</div>
              <div className="ad-k__content">
                <small>Average duration</small>
                <div className="ad-v">
                  <b>{metrics.avgDurationMinutes}m 14s</b>
                </div>
                <span className="ad-nt">Target: 10 min · pace optimal</span>
              </div>
            </div>

            <div className="ad-k">
              <div className="ad-k__icon" style={{ background: '#F8FAFC', color: '#0F172A' }}>📊</div>
              <div className="ad-k__content" style={{ width: '100%' }}>
                <small>Result breakdown</small>
                <div className="ad-dist" style={{ marginTop: '4px' }}>
                  <div className="ad-dbar" style={{ display: 'flex', overflow: 'hidden', height: '8px', borderRadius: '4px', background: '#E2E8F0' }}>
                    {(metrics.distribution?.passed ?? 0) > 0 && (
                      <i style={{ width: `${passedPct}%`, background: 'var(--ok)' }} title={`Passed: ${metrics.distribution.passed} (${passedPct}%)`} />
                    )}
                    {(metrics.distribution?.notPassed ?? 0) > 0 && (
                      <i style={{ width: `${notPassedPct}%`, background: 'var(--bad)' }} title={`Not passed: ${metrics.distribution.notPassed} (${notPassedPct}%)`} />
                    )}
                    {(metrics.distribution?.inProgress ?? 0) > 0 && (
                      <i style={{ width: `${inProgressPct}%`, background: 'var(--blue)' }} title={`In progress: ${metrics.distribution.inProgress} (${inProgressPct}%)`} />
                    )}
                    {(metrics.distribution?.notStarted ?? 0) > 0 && (
                      <i style={{ width: `${notStartedPct}%`, background: '#94A3B8' }} title={`Not started: ${metrics.distribution.notStarted} (${notStartedPct}%)`} />
                    )}
                  </div>
                  <div className="ad-dl" style={{ fontSize: '11px', display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                    <div><i style={{ background: 'var(--ok)' }} />Passed<b>{metrics.distribution?.passed ?? metrics.passed}</b></div>
                    <div><i style={{ background: 'var(--bad)' }} />Not passed<b>{metrics.distribution?.notPassed ?? metrics.notPassed}</b></div>
                    <div><i style={{ background: 'var(--blue)' }} />In progress<b>{metrics.distribution?.inProgress ?? metrics.inProgress}</b></div>
                    <div><i style={{ background: '#94A3B8' }} />Not started<b>{metrics.distribution?.notStarted ?? metrics.notStarted}</b></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Split Competency Breakdown & Learner Status Distribution Strip */}
          <div className="ad-two2">
            <div className="adm-card">
              <div className="ad-ch">
                <div>
                  <h2>Competency performance</h2>
                  <small>Average score across completed learner attempts (1–5 scale)</small>
                </div>
              </div>
              <div style={{ padding: '16px 20px' }}>
                {competencies.length === 0 ? (
                  <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>
                    No competency evaluation data recorded yet for this scenario.
                  </div>
                ) : (
                  <div className="ad-cbar">
                    {competencies.map((comp) => {
                      const hasEvaluations = typeof comp.avgScore === 'number' && comp.avgScore > 0;
                      const score = hasEvaluations ? comp.avgScore : 0;
                      const targetScore = comp.targetScore || 3.5;
                      const isLow = hasEvaluations && score < targetScore;
                      const percentage = hasEvaluations ? Math.min(100, Math.round((score / 5) * 100)) : 0;
                      const targetPct = Math.min(100, Math.round((targetScore / 5) * 100));
                      return (
                        <div key={comp.key} className={`ad-cb3 ${isLow ? 'ad-lo' : ''}`}>
                          <span title={`Weight: ${comp.weight}% · ${comp.evaluationsCount !== undefined ? `${comp.evaluationsCount} attempt evaluations` : 'Calculated cohort average'}`}>{comp.name}</span>
                          <div className="ad-tr2">
                            <i style={{ width: `${percentage}%` }} />
                            <em style={{ left: `${targetPct}%` }} title={`Benchmark target: ${targetScore.toFixed(1)} / 5 (${targetPct}%)`} />
                          </div>
                          <b>{hasEvaluations ? `${score.toFixed(1)} / 5` : '—'}</b>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="ad-leg">
                  <span><i style={{ background: 'var(--blue)' }} /> Learner attempt average</span>
                  <span><i style={{ background: 'var(--warn)' }} /> Below benchmark</span>
                  <span><i style={{ background: 'var(--ink)' }} /> Benchmark target</span>
                </div>
              </div>
            </div>

            <div className="adm-card">
              <div className="ad-ch">
                <div>
                  <h2>Learner Status Distribution</h2>
                  <small>Active cohort progression & attempt statuses</small>
                </div>
              </div>
              <div style={{ padding: '16px 20px' }}>
                <div className="ad-dbar" style={{ height: '14px', borderRadius: '7px', marginBottom: '16px', display: 'flex', overflow: 'hidden', background: '#E2E8F0' }}>
                  {(metrics.distribution?.passed ?? 0) > 0 && (
                    <i style={{ width: `${passedPct}%`, background: 'var(--ok)' }} title={`Passed: ${metrics.distribution.passed} (${passedPct}%)`} />
                  )}
                  {(metrics.distribution?.notPassed ?? 0) > 0 && (
                    <i style={{ width: `${notPassedPct}%`, background: 'var(--bad)' }} title={`Not passed: ${metrics.distribution.notPassed} (${notPassedPct}%)`} />
                  )}
                  {(metrics.distribution?.inProgress ?? 0) > 0 && (
                    <i style={{ width: `${inProgressPct}%`, background: 'var(--blue)' }} title={`In progress: ${metrics.distribution.inProgress} (${inProgressPct}%)`} />
                  )}
                  {(metrics.distribution?.notStarted ?? 0) > 0 && (
                    <i style={{ width: `${notStartedPct}%`, background: '#94A3B8' }} title={`Not started: ${metrics.distribution.notStarted} (${notStartedPct}%)`} />
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div
                    onClick={() => setResultFilter(resultFilter === 'Passed' ? 'All' : 'Passed')}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: 'var(--ok-soft, #ECFDF5)',
                      border: resultFilter === 'Passed' ? '2px solid var(--ok)' : '1px solid #D1FAE5',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ok, #0F8A5F)' }}>Passed</span>
                      <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--ok, #0F8A5F)' }}>{metrics.distribution?.passed ?? 0}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{passedPct}% of learners</span>
                  </div>

                  <div
                    onClick={() => setResultFilter(resultFilter === 'Not passed' ? 'All' : 'Not passed')}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: 'var(--bad-soft, #FEF2F2)',
                      border: resultFilter === 'Not passed' ? '2px solid var(--bad)' : '1px solid #FEE2E2',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bad, #E03131)' }}>Not passed</span>
                      <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--bad, #E03131)' }}>{metrics.distribution?.notPassed ?? 0}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{notPassedPct}% of learners</span>
                  </div>

                  <div
                    onClick={() => setResultFilter(resultFilter === 'In progress' ? 'All' : 'In progress')}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: 'var(--blue-soft, #EEF2FF)',
                      border: resultFilter === 'In progress' ? '2px solid var(--blue)' : '1px solid #E0E7FF',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--blue, #3B5BDB)' }}>In progress</span>
                      <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--blue, #3B5BDB)' }}>{metrics.distribution?.inProgress ?? 0}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{inProgressPct}% of learners</span>
                  </div>

                  <div
                    onClick={() => setResultFilter(resultFilter === 'Not started' ? 'All' : 'Not started')}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: '#F8FAFC',
                      border: resultFilter === 'Not started' ? '2px solid #64748B' : '1px solid #E2E8F0',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Not started</span>
                      <span style={{ fontSize: '16px', fontWeight: 800, color: '#334155' }}>{metrics.distribution?.notStarted ?? 0}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{notStartedPct}% of learners</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 5. SUPER ADMIN: Expandable Tenant-wise Drilldown Table */}
          {isPlatformAdmin && selectedTenantId === 'all' && tenantReports.length > 0 && (
            <div className="adm-card">
              <div className="ad-ch">
                <div>
                  <h2>Organization Overview & Tenant Breakdown</h2>
                  <small>Click any organization row to expand and inspect learner-level details.</small>
                </div>
              </div>
              <div style={{ padding: '16px 20px' }}>
                {tenantReports.map((tenantGroup) => {
                  const isExpanded = expandedTenantIds.has(tenantGroup.tenantId);
                  const tenantLearners = tenantGroup.learners.filter((l) => {
                    if (teamFilter !== 'All' && l.team !== teamFilter) return false;
                    if (designationFilter !== 'All' && l.designation !== designationFilter) return false;
                    if (regionFilter !== 'All' && l.region !== regionFilter) return false;
                    if (resultFilter !== 'All' && l.result !== resultFilter) return false;
                    if (searchQuery.trim()) {
                      const q = searchQuery.toLowerCase().trim();
                      const matchName = (l.name || '').toLowerCase().includes(q);
                      const matchId = (l.employeeId || '').toLowerCase().includes(q);
                      const matchEmail = (l.email || '').toLowerCase().includes(q);
                      const matchTeam = (l.team || '').toLowerCase().includes(q);
                      const matchDesig = (l.designation || '').toLowerCase().includes(q);
                      if (!matchName && !matchId && !matchEmail && !matchTeam && !matchDesig) return false;
                    }
                    return true;
                  });

                  return (
                    <div key={tenantGroup.tenantId} className={`ad-tenant-accordion-row ${isExpanded ? 'ad-expanded' : ''}`}>
                      <div
                        className="ad-tenant-accordion-header"
                        onClick={() => toggleTenantAccordion(tenantGroup.tenantId)}
                      >
                        <div className="ad-tenant-info">
                          <div className="ad-tenant-avatar">
                            {tenantGroup.tenantName.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <b style={{ fontSize: '14px', color: 'var(--navy)' }}>{tenantGroup.tenantName}</b>
                            <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--faint)' }}>
                              ID: {tenantGroup.tenantSlug || tenantGroup.tenantId} · {tenantLearners.length} {tenantLearners.length === tenantGroup.learners.length ? 'learners' : `of ${tenantGroup.learners.length} learners`}
                            </span>
                          </div>
                        </div>

                        <div className="ad-tenant-metrics-summary">
                          <div className="ad-tenant-metric-item">
                            <small>Pass Rate</small>
                            <b style={{ color: tenantGroup.metrics.passRate >= 70 ? 'var(--ok)' : 'var(--warn)' }}>
                              {tenantGroup.metrics.passRate}%
                            </b>
                          </div>
                          <div className="ad-tenant-metric-item">
                            <small>Avg Score</small>
                            <b>{tenantGroup.metrics.avgScore}%</b>
                          </div>
                          <div className="ad-tenant-metric-item">
                            <small>Completed</small>
                            <b>{tenantGroup.metrics.completed} / {tenantGroup.metrics.assigned}</b>
                          </div>
                          <div className={`ad-accordion-chevron ${isExpanded ? 'ad-open' : ''}`}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Learner Table & Mobile Cards for Tenant */}
                      {isExpanded && (
                        <div className="ad-tenant-accordion-body">
                          <div className="ad-table-desktop-wrapper" style={{ overflowX: 'auto' }}>
                            <table className="ad-RP">
                              <thead>
                                <tr>
                                  <th>Learner</th>
                                  <th>Designation</th>
                                  <th className="ad-c">Attempts</th>
                                  <th className="ad-c">Score</th>
                                  {competencies.map((c) => (
                                    <th key={c.key} className="ad-c">{c.name.split(' ')[0]}</th>
                                  ))}
                                  <th>Result</th>
                                  <th>Last attempt</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {tenantLearners.length === 0 ? (
                                  <tr>
                                    <td colSpan={6 + competencies.length} style={{ textAlign: 'center', padding: '16px', color: 'var(--muted)' }}>
                                      No learners found matching active filters in this organization.
                                    </td>
                                  </tr>
                                ) : (
                                  tenantLearners.map((learner, lIdx) => {
                                    const initials = `${(learner.name.split(' ')[0]?.[0] || '').toUpperCase()}${(learner.name.split(' ')[1]?.[0] || '').toUpperCase()}` || 'U';
                                    const color = AVATAR_COLORS[lIdx % AVATAR_COLORS.length];
                                    return (
                                      <tr key={learner.userId || `${tenantGroup.tenantId}-row-${lIdx}`}>
                                        <td>
                                          <div className="ad-who4">
                                            <span className="ad-av" style={{ background: color }}>{initials}</span>
                                            <div>
                                              <b>{learner.name}</b>
                                              <span>{learner.employeeId} · {learner.team}</span>
                                            </div>
                                          </div>
                                        </td>
                                        <td>{learner.designation}</td>
                                        <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                          {learner.attemptsCount}/{Math.max(learner.maxAttempts || 3, learner.attemptsCount || 0)}
                                        </td>
                                        <td className="ad-c">
                                          <span className="ad-tot2">
                                            {learner.bestScore !== null ? `${learner.bestScore}%` : '–'}
                                          </span>
                                        </td>
                                        {competencies.map((c) => {
                                          const score = getLearnerCompScore(learner, c);
                                          return (
                                            <td key={c.key} className="ad-c">
                                              <span className={`ad-sc2 ${getScorePillClass(score)}`}>
                                                {score !== null ? score : '–'}
                                              </span>
                                            </td>
                                          );
                                        })}
                                        <td>
                                          <span className={`ad-res ${getResultBadgeClass(learner.result)}`}>
                                            <i />
                                            {learner.result}
                                          </span>
                                        </td>
                                        <td style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                                          {learner.lastAttemptDate}
                                        </td>
                                        <td>
                                          {learner.lastAttemptId && (
                                            <span
                                              className="ad-vr"
                                              onClick={() => navigate(`/attempts/${learner.lastAttemptId}/results`)}
                                            >
                                              View report
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Mobile Report Cards for Tenant Accordion */}
                          <div className="ad-report-cards-mobile">
                            {tenantLearners.length === 0 ? (
                              <div style={{ textAlign: 'center', padding: '16px', color: 'var(--muted)', fontSize: '13px' }}>
                                No learners found matching active filters in this organization.
                              </div>
                            ) : (
                              tenantLearners.map((learner, lIdx) => {
                                const initials = `${(learner.name.split(' ')[0]?.[0] || '').toUpperCase()}${(learner.name.split(' ')[1]?.[0] || '').toUpperCase()}` || 'U';
                                const color = AVATAR_COLORS[lIdx % AVATAR_COLORS.length];
                                return (
                                  <div key={learner.userId || `${tenantGroup.tenantId}-card-${lIdx}`} className="ad-report-card">
                                    <div className="ad-report-card__header">
                                      <div className="ad-who4">
                                        <span className="ad-av" style={{ background: color }}>{initials}</span>
                                        <div>
                                          <b>{learner.name}</b>
                                          <span>{learner.employeeId} · {learner.team}</span>
                                        </div>
                                      </div>
                                      <span className={`ad-res ${getResultBadgeClass(learner.result)}`}>
                                        <i />
                                        {learner.result}
                                      </span>
                                    </div>
                                    <div className="ad-report-card__details">
                                      <div className="ad-report-card__metric">
                                        <span className="ad-report-card__label">Designation</span>
                                        <span className="ad-report-card__value">{learner.designation || '–'}</span>
                                      </div>
                                      <div className="ad-report-card__metric">
                                        <span className="ad-report-card__label">Best Score</span>
                                        <span className="ad-report-card__value" style={{ fontWeight: 700, color: 'var(--blue)' }}>
                                          {learner.bestScore !== null ? `${learner.bestScore}%` : '–'}
                                        </span>
                                      </div>
                                      <div className="ad-report-card__metric">
                                        <span className="ad-report-card__label">Attempts</span>
                                        <span className="ad-report-card__value">{learner.attemptsCount}/{Math.max(learner.maxAttempts || 3, learner.attemptsCount || 0)}</span>
                                      </div>
                                      <div className="ad-report-card__metric">
                                        <span className="ad-report-card__label">Last Attempt</span>
                                        <span className="ad-report-card__value">{learner.lastAttemptDate || '–'}</span>
                                      </div>
                                    </div>
                                    {learner.lastAttemptId && (
                                      <button
                                        type="button"
                                        className="ad-report-card__action"
                                        onClick={() => navigate(`/attempts/${learner.lastAttemptId}/results`)}
                                      >
                                        <span>View Full Report</span>
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M5 12h14M12 5l7 7-7 7" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 6. Learner Results Table matching Prototype `table.ad-RP` */}
          <div className="adm-card">
            <div className="ad-ch">
              <div>
                <h2>Learner results</h2>
                <small>Score that counts: best attempt</small>
              </div>
              <div className="ad-srch" style={{ width: '240px', height: '34px' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  placeholder="Search learner"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="ad-table-desktop-wrapper" style={{ overflowX: 'auto' }}>
              <table className="ad-RP">
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Designation</th>
                    <th className="ad-c">Attempts</th>
                    <th className="ad-c">Score</th>
                    {competencies.map((c) => (
                      <th key={c.key} className="ad-c">{c.name.split(' ')[0]}</th>
                    ))}
                    <th>Result</th>
                    <th>Last attempt</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLearners.length === 0 ? (
                    <tr>
                      <td colSpan={6 + competencies.length} style={{ textAlign: 'center', padding: '32px', color: 'var(--muted)' }}>
                        No learner results match the active filters.
                      </td>
                    </tr>
                  ) : (
                    paginatedLearners.map((learner, idx) => {
                      const nameStr = learner.name || 'Learner';
                      const parts = nameStr.trim().split(/\s+/);
                      const initials = `${parts[0]?.[0] || 'L'}${parts[1]?.[0] || ''}`.toUpperCase() || 'L';
                      const color = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                      return (
                        <tr key={learner.userId || idx}>
                          <td>
                            <div className="ad-who4">
                              <span className="ad-av" style={{ background: color }}>{initials}</span>
                              <div>
                                <b>{learner.name || 'Learner'}</b>
                                <span>{learner.employeeId || 'EMP'} · {learner.team || 'Sales'}</span>
                              </div>
                            </div>
                          </td>
                          <td>{learner.designation || 'Learner'}</td>
                          <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {learner.attemptsCount || 0}/{Math.max(learner.maxAttempts || 3, learner.attemptsCount || 0)}
                          </td>
                          <td className="ad-c">
                            <span className="ad-tot2">
                              {learner.bestScore !== null && learner.bestScore !== undefined ? `${learner.bestScore}%` : '–'}
                            </span>
                          </td>
                          {competencies.map((c) => {
                            const score = getLearnerCompScore(learner, c);
                            return (
                              <td key={c.key} className="ad-c">
                                <span className={`ad-sc2 ${getScorePillClass(score)}`}>
                                  {score !== null ? score : '–'}
                                </span>
                              </td>
                            );
                          })}
                          <td>
                            <span className={`ad-res ${getResultBadgeClass(learner.result)}`}>
                              <i />
                              {learner.result}
                            </span>
                          </td>
                          <td style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                            {learner.lastAttemptDate}
                          </td>
                          <td>
                            {learner.lastAttemptId && (
                              <span
                                className="ad-vr"
                                onClick={() => navigate(`/attempts/${learner.lastAttemptId}/results`)}
                              >
                                View report
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Report Cards for Main Learner Table */}
            <div className="ad-report-cards-mobile">
              {paginatedLearners.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--muted)', fontSize: '13px' }}>
                  No learner results match the active filters.
                </div>
              ) : (
                paginatedLearners.map((learner, idx) => {
                  const nameStr = learner.name || 'Learner';
                  const parts = nameStr.trim().split(/\s+/);
                  const initials = `${parts[0]?.[0] || 'L'}${parts[1]?.[0] || ''}`.toUpperCase() || 'L';
                  const color = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                  return (
                    <div key={learner.userId || idx} className="ad-report-card">
                      <div className="ad-report-card__header">
                        <div className="ad-who4">
                          <span className="ad-av" style={{ background: color }}>{initials}</span>
                          <div>
                            <b>{learner.name || 'Learner'}</b>
                            <span>{learner.employeeId || 'EMP'} · {learner.team || 'Sales'}</span>
                          </div>
                        </div>
                        <span className={`ad-res ${getResultBadgeClass(learner.result)}`}>
                          <i />
                          {learner.result}
                        </span>
                      </div>

                      <div className="ad-report-card__details">
                        <div className="ad-report-card__metric">
                          <span className="ad-report-card__label">Designation</span>
                          <span className="ad-report-card__value">{learner.designation || '–'}</span>
                        </div>
                        <div className="ad-report-card__metric">
                          <span className="ad-report-card__label">Best Score</span>
                          <span className="ad-report-card__value" style={{ fontWeight: 700, color: 'var(--blue)' }}>
                            {learner.bestScore !== null && learner.bestScore !== undefined ? `${learner.bestScore}%` : '–'}
                          </span>
                        </div>
                        <div className="ad-report-card__metric">
                          <span className="ad-report-card__label">Attempts</span>
                          <span className="ad-report-card__value">{learner.attemptsCount || 0} / {Math.max(learner.maxAttempts || 3, learner.attemptsCount || 0)}</span>
                        </div>
                        <div className="ad-report-card__metric">
                          <span className="ad-report-card__label">Last Attempt</span>
                          <span className="ad-report-card__value">{learner.lastAttemptDate || '–'}</span>
                        </div>
                      </div>

                      {learner.lastAttemptId && (
                        <button
                          type="button"
                          className="ad-report-card__action"
                          onClick={() => navigate(`/attempts/${learner.lastAttemptId}/results`)}
                        >
                          <span>View Full Report</span>
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination Footer */}
            {filteredLearners.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalItems={filteredLearners.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setCurrentPage(1);
                }}
                showPageSizeSelector
                pageSizeOptions={[10, 20, 50, 100]}
                itemLabel="learners"
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
